import * as React from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, setBearer } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Shield, KeyRound, Info } from "lucide-react";

/**
 * SecuritySection — Settings → Security tab (Phase 16.3).
 *
 * Change-password only (session invalidation happens server-side via
 * the auth_epoch bump).  Everything below the change-password form is
 * informational and future-proofs the "sessions" list for Phase 17.
 */
export default function SecuritySection() {
  const { refresh, user } = useAuth();
  const [current, setCurrent] = React.useState("");
  const [next, setNext] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [saved, setSaved] = React.useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (next.length < 12) { setErr("New password must be at least 12 characters."); return; }
    if (next !== confirm) { setErr("Passwords don't match."); return; }
    if (next === current) { setErr("New password must differ from your current one."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: current, new_password: next,
      });
      if (data?.token) setBearer(data.token);
      await refresh();
      setSaved(true);
      setCurrent(""); setNext(""); setConfirm("");
      toast.success("Password changed — other sessions signed out.");
      setTimeout(() => setSaved(false), 3200);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || "Change failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="security-section">
      <section className="card-panel">
        <div className="card-panel-head">
          <div>
            <div className="micro-label">Account</div>
            <div className="mt-0.5 text-[14px] font-semibold text-brand-ink flex items-center gap-2">
              <KeyRound size={14} /> Change password
            </div>
          </div>
          {saved && (
            <span className="chip chip-emerald mono-num text-[10px]"
                  data-testid="security-saved-indicator">✓ Saved</span>
          )}
        </div>
        <div className="card-panel-body space-y-3">
          <div className="text-[12px] text-brand-muted leading-relaxed">
            Signing in as <span className="mono-num text-brand-ink">{user?.email}</span>.
            Changing your password immediately signs out every other browser
            and API session — this device stays signed in.
          </div>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3"
                data-testid="change-password-form">
            <div className="md:col-span-2">
              <label className="micro-label">Current password</label>
              <input type="password" required className="field-input mt-1"
                     value={current} onChange={(e) => setCurrent(e.target.value)}
                     autoComplete="current-password"
                     data-testid="security-current-password" />
            </div>
            <div>
              <label className="micro-label">New password</label>
              <input type="password" required minLength={12} className="field-input mt-1"
                     value={next} onChange={(e) => setNext(e.target.value)}
                     autoComplete="new-password"
                     data-testid="security-new-password" />
              <div className="mt-1 text-[10.5px] text-brand-muted">
                Min. <span className="mono-num">12</span> characters. Not your name or email.
              </div>
            </div>
            <div>
              <label className="micro-label">Confirm new password</label>
              <input type="password" required minLength={12} className="field-input mt-1"
                     value={confirm} onChange={(e) => setConfirm(e.target.value)}
                     autoComplete="new-password"
                     data-testid="security-confirm-password" />
            </div>
            {err && (
              <div className="md:col-span-2 text-[12px] text-brand-coral"
                   data-testid="security-error">{err}</div>
            )}
            <div className="md:col-span-2">
              <button type="submit" className="btn-violet" disabled={busy || !current || !next || !confirm}
                      data-testid="security-submit">
                {busy ? "Saving…" : "Change password"}
              </button>
            </div>
          </form>
        </div>
      </section>

      <section className="card-panel">
        <div className="card-panel-head">
          <div>
            <div className="micro-label">Session policy</div>
            <div className="mt-0.5 text-[14px] font-semibold text-brand-ink flex items-center gap-2">
              <Shield size={14} /> How Fable protects your account
            </div>
          </div>
        </div>
        <div className="card-panel-body">
          <ul className="text-[12.5px] text-brand-ink leading-relaxed space-y-2">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-brand-violet"><Info size={12} /></span>
              <span>
                Password rotations bump your <span className="mono-num">auth_epoch</span>.
                Every JWT and cookie issued before the change is invalidated instantly.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-brand-violet"><Info size={12} /></span>
              <span>
                Failed logins trigger a sliding backoff — up to <span className="mono-num">30 seconds</span>
                {" "}between attempts — but never a hard lock. This blocks brute-force without letting
                attackers deny you access.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-brand-violet"><Info size={12} /></span>
              <span>
                Reset links live for 45 minutes (15 minutes when initiated by an admin) and are
                single-use. Every consumption is stamped in the security audit trail.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-brand-violet"><Info size={12} /></span>
              <span>
                Reset tokens are stored hashed (SHA-256). The raw value only exists
                in the emailed link — never in database rows or backend logs.
              </span>
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
}
